
import { Component, Vue } from "vue-property-decorator";
import vSelectControl from "@/components/vSelect.vue";
import vTable from "@/components/vTable.vue";
import CancellationWorkflowRepository from "@/utils/API/CancellationWorkflowRepository";
import { RetentionReportEntryDto } from "@/models/CancellationWorkflow";
import { TRANSLATIONS_FETCH } from "@/store/ActionTypes";

type RetentionReportFilters = {
  query: string;
  dateFrom: string;
  dateTo: string;
  packageName: string;
  couponCode: string;
  issueType: string;
  outcomeStatus: string;
};

type ReportEntry = RetentionReportEntryDto & {
  activityOnDate: Date;
};

type TableCell = {
  component: string;
  props: Record<string, unknown>;
  events: Record<string, unknown>;
  html?: string;
  onClick: (id: number) => void;
};

const REPORT_SETTINGS_STORAGE_KEY = "menusano-admin-retention-report-settings";
const REPORT_PAGE_SIZE = 10;
const ISSUE_OPTIONS = [
  "I no longer need the service",
  "It's too expensive",
  "Concerns with label accuracy or appearance",
  "Switching to a different service",
  "Lack of essential features (please specify below)",
  "Issues with customer support (please specify below)",
  "Other (please provide details below)",
];
const OUTCOME_OPTIONS = [
  "Offer Accepted",
  "Follow-up Pending",
  "Cancelled",
];

function createDefaultFilters(): RetentionReportFilters {
  return {
    query: "",
    dateFrom: "",
    dateTo: "",
    packageName: "",
    couponCode: "",
    issueType: "",
    outcomeStatus: "",
  };
}

@Component({
  components: {
    vSelectControl,
    vTable,
  },
})
class RetentionReport extends Vue {
  menuSanoLogo = require("@/assets/img/logo-apple.png");
  readonly reportPageSize = REPORT_PAGE_SIZE;
  draftFilters: RetentionReportFilters = createDefaultFilters();
  appliedFilters: RetentionReportFilters = createDefaultFilters();
  reportEntries: ReportEntry[] = [];
  isLoading = false;
  savedSettingsAt = "";
  currentPage = 1;

  mounted() {
    this.$store.dispatch(TRANSLATIONS_FETCH, {
      category: "pages",
      collections: ["common"],
    });
    this.restoreSavedSettings();
    this.loadReport();
  }

  get filteredEntries(): ReportEntry[] {
    const query = this.appliedFilters.query.trim().toLowerCase();
    const fromDate = this.appliedFilters.dateFrom ? new Date(`${this.appliedFilters.dateFrom}T00:00:00`) : null;
    const toDate = this.appliedFilters.dateTo ? new Date(`${this.appliedFilters.dateTo}T23:59:59`) : null;
    const packageName = this.normalizeFilterValue(this.appliedFilters.packageName);
    const couponCode = this.normalizeFilterValue(this.appliedFilters.couponCode);
    const issueType = this.normalizeFilterValue(this.appliedFilters.issueType);
    const outcomeStatus = this.normalizeFilterValue(this.appliedFilters.outcomeStatus);

    return this.reportEntries
      .filter((entry) => {
        const searchableValues = [
          entry.companyName,
          entry.customerName,
          entry.customerEmail,
          entry.packageName,
          entry.issueType,
          entry.couponCode,
          entry.outcomeStatus,
        ].map((value) => (value || "").toLowerCase());

        const queryMatches = !query || searchableValues.some((value) => value.includes(query));
        const fromMatches = !fromDate || entry.activityOnDate >= fromDate;
        const toMatches = !toDate || entry.activityOnDate <= toDate;
        const packageMatches = !packageName || entry.packageName === packageName;
        const couponMatches = !couponCode || entry.couponCode === couponCode;
        const issueMatches = !issueType || entry.issueType === issueType;
        const outcomeMatches = !outcomeStatus || entry.outcomeStatus === outcomeStatus;

        return queryMatches
          && fromMatches
          && toMatches
          && packageMatches
          && couponMatches
          && issueMatches
          && outcomeMatches;
      })
      .sort((left, right) => right.activityOnDate.getTime() - left.activityOnDate.getTime());
  }

  get acceptedCount(): number {
    return this.filteredEntries.filter((entry) => entry.outcomeStatus === "Offer Accepted").length;
  }

  get followUpPendingCount(): number {
    return this.filteredEntries.filter((entry) => entry.outcomeStatus === "Follow-up Pending").length;
  }

  get cancelledCount(): number {
    return this.filteredEntries.filter((entry) => entry.outcomeStatus === "Cancelled").length;
  }

  get printGeneratedAt(): string {
    return this.$moment().format("MMM DD, YYYY h:mm A");
  }

  get packageFilterOptions() {
    return this.buildFilterOptions(this.uniqueValues(this.reportEntries.map((entry) => entry.packageName)));
  }

  get couponFilterOptions() {
    return this.buildFilterOptions(this.uniqueValues(this.reportEntries.map((entry) => entry.couponCode).filter(Boolean)));
  }

  get issueFilterOptions() {
    return this.buildFilterOptions(ISSUE_OPTIONS);
  }

  get outcomeFilterOptions() {
    return this.buildFilterOptions(OUTCOME_OPTIONS);
  }

  get selectedPackageFilterValue() {
    return this.findSelectedOption(this.packageFilterOptions, this.draftFilters.packageName);
  }

  get selectedCouponFilterValue() {
    return this.findSelectedOption(this.couponFilterOptions, this.draftFilters.couponCode);
  }

  get selectedIssueFilterValue() {
    return this.findSelectedOption(this.issueFilterOptions, this.draftFilters.issueType);
  }

  get selectedOutcomeFilterValue() {
    return this.findSelectedOption(this.outcomeFilterOptions, this.draftFilters.outcomeStatus);
  }

  get tableHeaders() {
    return [
      { name: "Date", key: "Date", searchable: false, searchString: "", sortable: false },
      { name: "Customer", key: "Customer", searchable: false, searchString: "", sortable: false },
      { name: "Package", key: "Package", searchable: false, searchString: "", sortable: false },
      { name: "Issue", key: "Issue", searchable: false, searchString: "", sortable: false },
      { name: "Coupon Used", key: "CouponUsed", searchable: false, searchString: "", sortable: false },
      { name: "Outcome (Action)", key: "Outcome", searchable: false, searchString: "", sortable: false },
    ];
  }

  get paginatedEntries(): ReportEntry[] {
    const startIndex = (this.currentPage - 1) * this.reportPageSize;
    return this.filteredEntries.slice(startIndex, startIndex + this.reportPageSize);
  }

  get tableRows() {
    return this.paginatedEntries.map((entry) => ({
      e1: `${this.formatDate(entry.activityOn)} | ${entry.companyName}`,
      e2: `${entry.customerName} | ${entry.customerEmail}`,
      e3: entry.packageName,
      e4: entry.issueType,
      e5: this.buildCouponCell(entry.couponCode),
      e6: this.buildOutcomeCell(entry.outcomeStatus),
      meta: {
        id: entry.id,
      },
    }));
  }

  loadReport() {
    this.isLoading = true;

    CancellationWorkflowRepository.getRetentionReport()
      .then((response) => {
        const result = Array.isArray(response.data.result) ? response.data.result : [];
        this.reportEntries = result.map((entry) => ({
          ...entry,
          activityOnDate: new Date(entry.activityOn),
        }));
        this.currentPage = 1;
      })
      .catch(() => {
        this.reportEntries = [];
        if (this.$snotify?.error) {
          this.$snotify.error("We couldn’t load the retention report right now.");
        }
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  saveSettings() {
    window.localStorage.setItem(
      REPORT_SETTINGS_STORAGE_KEY,
      JSON.stringify({
        filters: this.draftFilters,
        savedAt: new Date().toISOString(),
      })
    );

    this.savedSettingsAt = this.$moment().format("MMM DD, YYYY h:mm A");
    if (this.$snotify?.success) {
      this.$snotify.success("Retention report settings saved.");
    }
  }

  restoreSavedSettings() {
    const savedValue = window.localStorage.getItem(REPORT_SETTINGS_STORAGE_KEY);
    if (!savedValue) {
      return;
    }

    try {
      const parsed = JSON.parse(savedValue);
      this.draftFilters = {
        ...createDefaultFilters(),
        ...(parsed?.filters || {}),
      };
      this.appliedFilters = {
        ...this.draftFilters,
      };
      this.savedSettingsAt = parsed?.savedAt
        ? this.$moment(parsed.savedAt).format("MMM DD, YYYY h:mm A")
        : "";
    } catch (error) {
      this.draftFilters = createDefaultFilters();
      this.appliedFilters = createDefaultFilters();
      this.savedSettingsAt = "";
    }
  }

  resetFilters() {
    this.draftFilters = createDefaultFilters();
    this.appliedFilters = createDefaultFilters();
    this.savedSettingsAt = "";
    this.currentPage = 1;
    window.localStorage.removeItem(REPORT_SETTINGS_STORAGE_KEY);

    if (this.$snotify?.success) {
      this.$snotify.success("Retention report filters reset.");
    }
  }

  generateReport() {
    this.appliedFilters = {
      ...this.draftFilters,
    };
    this.currentPage = 1;
  }

  handlePageChange([page]: [number, number, unknown[], unknown]) {
    this.currentPage = page;
  }

  printReport() {
    window.print();
  }

  goBackToWorkflow() {
    this.$router.push({ name: "cancellation-workflow" });
  }

  buildFilterOptions(values: string[]) {
    return [
      { id: "", name: "All" },
      ...values.map((value) => ({ id: value, name: value })),
    ];
  }

  normalizeFilterValue(value: unknown): string {
    if (typeof value === "string") {
      return value === "All" ? "" : value;
    }

    if (value && typeof value === "object") {
      const option = value as { id?: unknown; name?: unknown };
      if (typeof option.id === "string") {
        return option.id === "All" ? "" : option.id;
      }

      if (typeof option.name === "string") {
        return option.name === "All" ? "" : option.name;
      }
    }

    return "";
  }

  findSelectedOption(options: Array<{ id: string; name: string }>, value: unknown) {
    const normalizedValue = this.normalizeFilterValue(value);

    return options.find((option) => option.id === normalizedValue)
      || options[0]
      || null;
  }

  uniqueValues(values: string[]): string[] {
    return Array.from(new Set(values.filter(Boolean))).sort((left, right) => left.localeCompare(right));
  }

  buildCouponCell(couponCode: string): TableCell {
    return {
      component: "span",
      props: {
        class: "report-coupon-pill",
      },
      events: {},
      html: couponCode || "No coupon",
      onClick: () => undefined,
    };
  }

  buildOutcomeCell(outcomeStatus: string): TableCell {
    return {
      component: "span",
      props: {
        class: this.getOutcomeStatusClass(outcomeStatus),
      },
      events: {},
      html: outcomeStatus,
      onClick: () => undefined,
    };
  }

  getOutcomeStatusClass(outcomeStatus: string): string {
    return {
      "Offer Accepted": "report-status-pill report-status-pill-accepted",
      "Follow-up Pending": "report-status-pill report-status-pill-followup",
      "Cancelled": "report-status-pill report-status-pill-cancelled",
      "Still deciding": "report-status-pill report-status-pill-viewed",
    }[outcomeStatus] || "report-status-pill report-status-pill-viewed";
  }

  formatDate(value: string): string {
    return this.$moment(value).format("MMM DD, YYYY h:mm A");
  }
}

export default RetentionReport;
